import { apiGet, apiPut, apiPost } from "helpers/Request";
import { useAuth } from "hooks/Auth";
import React, {
    Fragment,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import LowSelectBox from "components/FormElements/LowSelectBox";
import { ExternalLinkIcon } from "@heroicons/react/outline";
import aclResources from "assets/acl-resources.json";
import { forceInt } from "Utils";
import GroupSelector from "./Groups/GroupSelector";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { toast } from "react-hot-toast";
import SecondaryButton from "components/Buttons/SecondaryButton";
import { modal } from "components/HeadlessModal";

function transformAcl(acl) {
    const accesses = acl.accesses;
    let newAccess = {};
    Object.keys(accesses).forEach((key) => {
        const actions = accesses[key];
        let newActions = {};
        actions.forEach((action) => {
            newActions[action.action] = forceInt(action.allowed);
        });
        newAccess[key] = newActions;
    });

    return Object.assign({}, acl, { accesses: newAccess });
}
function unTransformAcl(acl) {
    const accesses = acl.accesses;
    let newAccess = {};
    Object.keys(accesses).forEach((key) => {
        let newActions = [];
        Object.keys(accesses[key]).forEach((action) => {
            if (accesses[key][action] === 1 || accesses[key][action] === 0) {
                newActions.push({
                    action: action,
                    allowed: accesses[key][action],
                });
            }
        });
        newAccess[key] = newActions;
    });

    return Object.assign({}, acl, { accesses: newAccess });
}

export default function GroupsPanel(props) {
    const auth = useAuth();

    const inputRef = useRef();
    const [roles, setRoles] = useState([]);
    const [role, setRole] = useState("Select");
    const [selectedRole, setSelectedRole] = useState({});
    const [showRows, setShowRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const updateRole = (acl, resource, value) => {
        let newAccess = {};
        let newAccessList = {};
        if (value !== undefined) {
            newAccess[resource] = value;
            newAccessList = Object.assign(
                {},
                selectedRole.accesses[acl],
                newAccess,
                aclResources.required[acl] ? aclResources.required[acl] : {}
            );
        } else {
            newAccessList = Object.assign(
                {},
                selectedRole.accesses[acl],
                aclResources.required[acl] ? aclResources.required[acl] : {}
            );
            delete newAccess[resource];
        }

        let newAcl = selectedRole.accesses;
        newAcl[acl] = newAccessList;
        setSelectedRole(Object.assign({}, selectedRole, { accesses: newAcl }));
    };

    const handleSave = () => {
        setSaving(true);
        const acl = unTransformAcl(selectedRole);

        apiPut(`/v4/acl/${role}`, auth.publication, acl).then((res) => {
            setSaving(false);
            toast.success("Role updated!");
        });
    };

    const toggleShown = (acl) => {
        if (showRows.includes(acl)) {
            setShowRows(showRows.filter((item) => item !== acl));
        } else {
            setShowRows([...showRows, acl]);
        }
    };

    const requestNew = () => {
        modal({
            title: "New Group",
            children: (
                <Fragment>
                    <div className="mb-2">
                        Groups cannot be removed after they have been created.
                        Please use with caution.
                    </div>
                    <div>
                        <input
                            type="text"
                            className="block w-full rounded-md border-gray-300 py-2 px-4 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-white sm:text-sm"
                            ref={inputRef}
                            placeholder="Group Name"
                        />
                    </div>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <PrimaryButton onClick={onCreate}>Create</PrimaryButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const onCreate = () => {
        modal.update({ disabled: true });
        const id = toast.loading("Creating group...");

        const name = inputRef.current.value;
        apiPost("/v4/acl", auth.publication, { name: name }).then((res) => {
            modal.dismiss();
            toast.success("Group created!", { id });
            setRoles([...roles, res[0]]);
        });
    };

    useLayoutEffect(() => {
        if (role === "Select" || !role) {
            return;
        }
        apiGet(`/v4/acl/${role}`, auth.publication).then((resp) => {
            setSelectedRole(transformAcl(resp[0]));
        });
    }, [role, auth.publication]);

    useEffect(() => {
        apiGet("/v4/acl", auth.publication).then((resp) => {
            setRoles(resp);
        });
    }, [auth.publication]);

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex-grow overflow-auto p-4">
                <div className="w-full space-y-8">
                    <div className="prose text-sm">
                        Manage group access levels. Users and group assignments
                        are managed in{" "}
                        <a
                            href={`${process.env.REACT_APP_ACCOUNTS_DOMAIN}/app/user`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 underline"
                        >
                            SNworks Accounts
                            <ExternalLinkIcon className="ml-1 -mt-1 inline-block h-4 w-4" />
                        </a>
                        .
                    </div>

                    <div className="flex items-end space-x-4">
                        <div className="flex-grow">
                            <LowSelectBox
                                label="Group Name"
                                value={role}
                                options={roles
                                    .filter((r) => r.name !== "Administrator")
                                    .map((r) => r.name)}
                                onChange={setRole}
                            />
                        </div>
                        <div>
                            <SecondaryButton onClick={requestNew}>
                                New Group
                            </SecondaryButton>
                        </div>
                    </div>

                    {role !== "Select" && (
                        <Fragment>
                            <div className="mt-8 flex flex-col">
                                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div className="overflow-hidden">
                                            <table className="min-w-full">
                                                <thead className="bg-white">
                                                    <tr>
                                                        <th
                                                            scope="col"
                                                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                                                        >
                                                            Access
                                                        </th>
                                                        <th
                                                            scope="col"
                                                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                                        >
                                                            Action
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {role !== "Select" &&
                                                        Object.keys(
                                                            aclResources.optional
                                                        ).map((name) => (
                                                            <Fragment
                                                                key={name}
                                                            >
                                                                <tr className="border-t border-gray-200">
                                                                    <th
                                                                        colSpan={
                                                                            2
                                                                        }
                                                                        scope="colgroup"
                                                                        className="bg-gray-50 px-4 py-2 text-left font-semibold text-gray-900 sm:px-6"
                                                                    >
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                toggleShown(
                                                                                    name
                                                                                )
                                                                            }
                                                                        >
                                                                            {
                                                                                name
                                                                            }
                                                                        </button>
                                                                    </th>
                                                                </tr>
                                                                {Object.keys(
                                                                    aclResources
                                                                        .optional[
                                                                        name
                                                                    ]
                                                                ).map(
                                                                    (
                                                                        resource
                                                                    ) => (
                                                                        <GroupSelector
                                                                            key={`${name}.${resource}`}
                                                                            currentRole={
                                                                                selectedRole
                                                                            }
                                                                            hidden={
                                                                                !showRows.includes(
                                                                                    name
                                                                                )
                                                                            }
                                                                            role={
                                                                                name
                                                                            }
                                                                            resource={
                                                                                resource
                                                                            }
                                                                            updateRole={
                                                                                updateRole
                                                                            }
                                                                        />
                                                                    )
                                                                )}
                                                            </Fragment>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    )}
                </div>
            </div>
            <div className="w-full bg-gray-50 p-2 text-right">
                <PrimaryButton
                    onClick={handleSave}
                    blocking={saving}
                    disabled={role === "Select"}
                >
                    Save Changes
                </PrimaryButton>
            </div>
        </div>
    );
}
